import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Navigation, Route, Fuel, TrendingUp, Plus, ChevronRight,
  Car, Receipt, FileText, BarChart3, Settings, LogOut,
  Bell, Menu, X, Play, MapPin, Clock, Euro, Loader2,
  ExternalLink, Smartphone
} from 'lucide-react';

const OptiTrajetDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchDashboard = useCallback(async () => {
    const token = localStorage.getItem('optitrajet_token');
    if (!token) {
      navigate('/optitrajet/login');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/optitrajet/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('optitrajet_token');
        navigate('/optitrajet/login');
        return;
      }

      const data = await response.json();
      setDashboard(data);
      setUser(data.user);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, navigate]);

  useEffect(() => {
    setMounted(true);
    const storedUser = localStorage.getItem('optitrajet_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchDashboard();
  }, [fetchDashboard]);

  const handleLogout = () => {
    localStorage.removeItem('optitrajet_token');
    localStorage.removeItem('optitrajet_user');
    navigate('/optitrajet/login');
  };

  const openExternalGPS = (destination) => {
    // Detect platform and open appropriate maps app
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    const encodedDest = encodeURIComponent(destination || '');
    
    if (isIOS) {
      // Apple Maps
      window.open(`maps://maps.apple.com/?daddr=${encodedDest}`, '_blank');
    } else if (isAndroid) {
      // Google Maps on Android
      window.open(`google.navigation:q=${encodedDest}`, '_blank');
    } else {
      // Fallback to Google Maps web
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodedDest}`, '_blank');
    }
  };

  const stats = dashboard?.stats ? [
    { 
      label: 'Trajets ce mois', 
      value: dashboard.stats.trips_this_month, 
      icon: Route, 
      color: '#2563EB', 
      bg: '#EFF6FF',
      trend: dashboard.stats.trips_trend
    },
    { 
      label: 'Distance totale', 
      value: `${dashboard.stats.total_distance_km.toLocaleString()} km`, 
      icon: Navigation, 
      color: '#10B981', 
      bg: '#ECFDF5' 
    },
    { 
      label: 'Dépenses', 
      value: `${dashboard.stats.total_expenses.toLocaleString()} €`, 
      icon: Fuel, 
      color: '#F59E0B', 
      bg: '#FFFBEB' 
    },
    { 
      label: 'Revenus', 
      value: `${dashboard.stats.total_revenue.toLocaleString()} €`, 
      icon: TrendingUp, 
      color: '#8B5CF6', 
      bg: '#F5F3FF' 
    },
  ] : [];

  const menuItems = [
    { icon: Route, label: 'Dashboard', path: '/optitrajet/dashboard', active: true },
    { icon: MapPin, label: 'Trajets', path: '/optitrajet/trips' },
    { icon: Receipt, label: 'Dépenses', path: '/optitrajet/expenses' },
    { icon: FileText, label: 'Factures', path: '/optitrajet/invoices' },
    { icon: BarChart3, label: 'Rapports', path: '/optitrajet/reports' },
    { icon: Car, label: 'Véhicules', path: '/optitrajet/vehicles' },
    { icon: Settings, label: 'Paramètres', path: '/optitrajet/settings' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8FAFC' }}>
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <Navigation className="h-8 w-8 text-white" />
          </div>
          <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F8FAFC', fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <Menu className="h-5 w-5 text-slate-600" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200">
              <Navigation className="h-5 w-5 text-white" />
            </div>
            <span style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold text-slate-800">
              OptiTrajet
            </span>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="p-2 rounded-lg hover:bg-slate-100 transition-colors relative">
              <Bell className="h-5 w-5 text-slate-500" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
            </button>
            <div 
              className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-sm font-medium cursor-pointer"
              onClick={() => navigate('/optitrajet/settings')}
            >
              {user?.full_name?.charAt(0) || 'U'}
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:flex flex-col w-64 border-r border-slate-200 bg-white min-h-[calc(100vh-64px)] p-4 sticky top-16">
          <nav className="space-y-1 flex-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                    item.active 
                      ? 'bg-blue-50 text-blue-700 font-medium' 
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${item.active ? 'text-blue-600' : 'text-slate-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all mt-4"
          >
            <LogOut className="h-5 w-5" />
            Déconnexion
          </button>
        </aside>

        {/* Mobile Sidebar */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white p-4 shadow-2xl animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center">
                    <Navigation className="h-5 w-5 text-white" />
                  </div>
                  <span style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-bold">OptiTrajet</span>
                </div>
                <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-slate-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="space-y-1">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        item.active ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <Icon className={`h-5 w-5 ${item.active ? 'text-blue-600' : 'text-slate-400'}`} />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-red-50 hover:text-red-600 transition-all mt-6 w-full"
              >
                <LogOut className="h-5 w-5" />
                Déconnexion
              </button>
            </aside>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 pb-24 lg:pb-8">
          {/* Welcome */}
          <div className={`mb-8 transition-all duration-500 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h1 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-2xl lg:text-3xl font-bold text-slate-800 mb-2">
              Bonjour, {user?.full_name?.split(' ')[0] || 'Utilisateur'} 👋
            </h1>
            <p className="text-slate-500">
              Prêt pour la route ? Voici votre résumé du jour.
            </p>
          </div>

          {/* Start Trip CTA */}
          <Card 
            className={`mb-8 overflow-hidden border-0 shadow-xl transition-all duration-500 delay-100 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            style={{ background: 'linear-gradient(135deg, #2563EB 0%, #4F46E5 50%, #7C3AED 100%)' }}
          >
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-white">
                  <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-xl font-semibold mb-1">
                    Démarrer un trajet
                  </h2>
                  <p className="text-blue-100 text-sm flex items-center gap-2">
                    <Smartphone className="h-4 w-4" />
                    Ouvre Google Maps ou Apple Maps
                  </p>
                </div>
                <div className="flex gap-3">
                  <Link to="/optitrajet/trips/new">
                    <Button
                      size="lg"
                      className="h-14 px-6 rounded-xl bg-white hover:bg-slate-50 text-blue-700 font-semibold shadow-lg transition-all hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
                      data-testid="start-trip-btn"
                    >
                      <Play className="h-5 w-5 mr-2 fill-current" />
                      Nouveau trajet
                    </Button>
                  </Link>
                </div>
              </div>
              
              {/* Plan limit warning */}
              {user?.subscription_plan === 'free' && user?.trips_limit && (
                <div className="mt-4 pt-4 border-t border-white/20">
                  <div className="flex items-center justify-between text-white/90 text-sm">
                    <span>Trajets utilisés ce mois</span>
                    <span className="font-semibold">{user.trips_used || 0} / {user.trips_limit}</span>
                  </div>
                  <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-white rounded-full transition-all"
                      style={{ width: `${((user.trips_used || 0) / user.trips_limit) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stats Grid */}
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8 transition-all duration-500 delay-200 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
                <Card 
                  key={idx} 
                  className="border border-slate-100 shadow-sm hover:shadow-md transition-all hover:-translate-y-1 cursor-pointer"
                  style={{ animationDelay: `${idx * 50}ms` }}
                >
                  <CardContent className="p-5">
                    <div 
                      className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                      style={{ backgroundColor: stat.bg }}
                    >
                      <Icon className="h-5 w-5" style={{ color: stat.color }} />
                    </div>
                    <p className="text-xs font-medium text-slate-500 mb-1">{stat.label}</p>
                    <div className="flex items-end gap-2">
                      <p style={{ fontFamily: "'Outfit', sans-serif" }} className="text-2xl font-bold text-slate-800">
                        {stat.value}
                      </p>
                      {stat.trend !== undefined && stat.trend !== 0 && (
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                          stat.trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {stat.trend > 0 ? '+' : ''}{stat.trend}%
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Recent Trips */}
          <div className={`mb-8 transition-all duration-500 delay-300 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800">
                Trajets récents
              </h2>
              <Link to="/optitrajet/trips" className="text-sm font-medium text-blue-600 flex items-center gap-1 hover:underline">
                Voir tout <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            
            {dashboard?.recent_trips?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.recent_trips.map((trip, idx) => (
                  <Card 
                    key={trip.id || idx} 
                    className="border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                          <Route className="h-6 w-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 truncate">
                            {trip.start_address} → {trip.end_address || 'En cours'}
                          </p>
                          <p className="text-sm text-slate-500 flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <Navigation className="h-3 w-3" />
                              {trip.distance_km} km
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {trip.duration_minutes} min
                            </span>
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p style={{ fontFamily: "'Outfit', sans-serif" }} className="font-bold text-lg text-emerald-600">
                            {trip.amount} €
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border border-dashed border-slate-200 bg-slate-50/50">
                <CardContent className="p-8 text-center">
                  <Route className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-4">Aucun trajet enregistré</p>
                  <Link to="/optitrajet/trips/new">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="h-4 w-4 mr-2" />
                      Créer mon premier trajet
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Quick Actions */}
          <div className={`transition-all duration-500 delay-400 ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h2 style={{ fontFamily: "'Outfit', sans-serif" }} className="text-lg font-semibold text-slate-800 mb-4">
              Actions rapides
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: Receipt, label: 'Scanner un reçu', color: '#F59E0B', bg: '#FFFBEB', path: '/optitrajet/expenses/scan' },
                { icon: FileText, label: 'Créer une facture', color: '#8B5CF6', bg: '#F5F3FF', path: '/optitrajet/invoices/new' },
                { icon: BarChart3, label: 'Export comptable', color: '#10B981', bg: '#ECFDF5', path: '/optitrajet/reports' },
                { icon: Car, label: 'Mes véhicules', color: '#2563EB', bg: '#EFF6FF', path: '/optitrajet/vehicles' },
              ].map((action, idx) => {
                const Icon = action.icon;
                return (
                  <Link key={idx} to={action.path}>
                    <Card className="border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all cursor-pointer h-full">
                      <CardContent className="p-5 text-center">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                          style={{ backgroundColor: action.bg }}
                        >
                          <Icon className="h-6 w-6" style={{ color: action.color }} />
                        </div>
                        <p className="text-sm font-medium text-slate-700">{action.label}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-slate-200 h-16 flex items-center justify-around z-40 px-2">
        {[
          { icon: Route, label: 'Accueil', path: '/optitrajet/dashboard', active: true },
          { icon: MapPin, label: 'Trajets', path: '/optitrajet/trips' },
          { icon: Plus, label: '', path: '/optitrajet/trips/new', special: true },
          { icon: Receipt, label: 'Dépenses', path: '/optitrajet/expenses' },
          { icon: BarChart3, label: 'Rapports', path: '/optitrajet/reports' },
        ].map((item) => {
          const Icon = item.icon;
          
          if (item.special) {
            return (
              <Link key={item.path} to={item.path}>
                <button className="w-14 h-14 -mt-6 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-300 text-white transition-transform hover:scale-105 active:scale-95">
                  <Plus className="h-7 w-7" />
                </button>
              </Link>
            );
          }
          
          return (
            <Link key={item.path} to={item.path}>
              <button className={`flex flex-col items-center justify-center min-w-[60px] py-2 ${
                item.active ? 'text-blue-600' : 'text-slate-400'
              }`}>
                <Icon className="h-5 w-5" />
                <span className="text-[10px] mt-1 font-medium">{item.label}</span>
              </button>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

export default OptiTrajetDashboard;
